import {Injectable, Injector} from '@angular/core';
import {chunk} from 'lodash-es';
import {concatMap, defer, forkJoin, from, Observable, of, reduce, toArray} from 'rxjs';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {createPublicClient, defineChain, erc20Abi, Hex, http, isAddress, PublicClient, WalletClient} from 'viem';
import {AppConfigService} from '../../../services/config.service';
import {DynamicZyxDaemonService} from '../../dynamic-xyz/dynamic-zyx-daemon.service';
import {CommonBlockchainUtilService} from '../common-blockchain-util.service';
import {DEX_EMPTY_ADDR} from '../dex-streaming/const';
import {DexSecuritiesSubjectService} from '../dex-streaming/subjects/dex-securities-subject-service';
import {Deliverable, DeliverableId} from '../dex-streaming/types/deliverable';
import {adjustDp} from '../dex-streaming/utils';
import {BALANCE_FETCHING_TRIGGER} from './balance-fetching-trigger';
import {DeliverableBalance} from './deliverable-balance';

interface TokenFns {
    balanceOf: (account: string) => Promise<bigint>;
    allowance: (owner: string, spender: string) => Promise<bigint>;
}

interface TokenCacheRec {
    address: string;
    decimals: number;
    fns: TokenFns;
}

@Injectable()
export class Erc20BalancesService {

    private deliverablesCache: Deliverable[];
    private deliverablesCacheExpireAt: number;
    private readonly deliverablesCacheLifespan = 1000 * 60 * 30; // 30 min

    private readonly tokensCache = new Map<string, TokenCacheRec>();

    private readonly publicClient = this.commonBlockchainUtilService.getPublicClient();
    private readonly publicClientReadBalance = this.createReadBalancePublicClient();

    private readonly nativeCurrency = this.appConfigService.appConfig.dex.chain.nativeCurrency;

    constructor(
        protected dynamicZyxDaemonService: DynamicZyxDaemonService,
        private dexSecuritiesSubjectService: DexSecuritiesSubjectService,
        private commonBlockchainUtilService: CommonBlockchainUtilService,
        private appConfigService: AppConfigService,
        private injector: Injector
    ) {}

    public fetchWalletTokenBalances(
        wAddresses: string,
        dIds?: DeliverableId[]
    ): Observable<DeliverableBalance[]> {

        // Support only 0x... addresses (EVM)
        if (!isAddress(wAddresses)) {
            return of([]);
        }

        const deliverables = dIds
            ? dIds.map(dId => this.dexSecuritiesSubjectService.getDeliverableById(dId))
            : this.getCachedDeliverables();

        return this.getBalancesWithPagination(wAddresses, deliverables);
    }

    public fetchWalletsTokenBalances(
        wAddresses: string[],
        dIds?: DeliverableId[]
    ): Observable<Record<string, DeliverableBalance[]>> {
        const requestsDict = wAddresses.reduce(
            (dict, wAddr) => {
                dict[wAddr] = this.fetchWalletTokenBalances(wAddr, dIds).pipe(
                    catchError(error => {
                        console.error(error);

                        return of(undefined);
                    })
                );

                return dict;
            },
            {} as Record<string, Observable<DeliverableBalance[]>>
        );

        return forkJoin(requestsDict);
    }

    public fetchDeliverableBalance(wAddress: string, d: Deliverable): Observable<DeliverableBalance | undefined> {

        // Support only 0x... addresses (EVM)
        if (!isAddress(wAddress)) {
            return of(undefined);
        }

        return fromPromise(this.readDeliverableBalance(wAddress, d));
    }

    public async convertDelUnitsToToken(d: Deliverable, dValue: bigint): Promise<bigint> {
        try {
            const tokenCacheRec = await this.getOrCreateTokenCacheRec(d);

            return adjustDp(dValue, d.decimalPlaces, tokenCacheRec.decimals);
        } catch (e) {
            console.error(`Error reading token ${d.symbol} (${d.identifier}):`, e);

            return 0n;
        }
    }

    public async getAllowance(params: Readonly<{
        d: Deliverable;
        owner: string;
        spender: string;
    }>): Promise<bigint> {
        const tokenCacheRec = await this.getOrCreateTokenCacheRec(params.d);

        return await tokenCacheRec.fns.allowance(params.owner, params.spender);
    }

    public async approveSpending(params: Readonly<{
        d: Deliverable;
        dQty?: bigint;
        tokenQty?: bigint;
        owner: string;
        spender: Hex;
        walletClient: WalletClient;
    }>): Promise<void> {
        const tokenCacheRec = await this.getOrCreateTokenCacheRec(params.d);
        let tokenQtyToApprove: bigint = params.tokenQty;

        if (!params.tokenQty) {
            const tokenDp = tokenCacheRec.decimals;
            tokenQtyToApprove = adjustDp(params.dQty, params.d.decimalPlaces, tokenDp);
        }

        const currentAllowance = await this.getAllowance(params);

        if (currentAllowance < tokenQtyToApprove) {
            const txHash = await params.walletClient.writeContract({
                address:      params.d.identifier as Hex,
                abi:          erc20Abi,
                functionName: 'approve',
                args:         [
                    params.spender,
                    tokenQtyToApprove
                ],
                account:      params.walletClient.account,
                chain:        params.walletClient.chain
            });
            await this.publicClient.waitForTransactionReceipt({
                hash:          txHash,
                confirmations: 1
            });

            this.notifyNativeBalanceUpdateNeeded(params.owner);
        }
    }

    public getCachedDeliverables(): Deliverable[] {
        let list: Deliverable[];
        const ts = Date.now();

        if (this.deliverablesCache && this.deliverablesCacheExpireAt > ts) {
            list = this.deliverablesCache;
        } else {
            list = this.createDeliverableList();
            const ts = Date.now();

            this.deliverablesCache = list;
            this.deliverablesCacheExpireAt = ts + this.deliverablesCacheLifespan;
        }

        return list;
    }

    protected async readDeliverableBalance(
        wAddress: string,
        d: Deliverable
    ): Promise<DeliverableBalance> {
        return this.isNativeDel(d)
            ? this.readNativeDelBalance(wAddress, d)
            : this.readErc20DelBalance(wAddress, d);
    }

    private async readErc20DelBalance(
        wAddress: string,
        d: Deliverable
    ): Promise<DeliverableBalance> {
        let balance: bigint | undefined = undefined;
        let tokenCacheRec: TokenCacheRec;

        try {
            tokenCacheRec = await this.getOrCreateTokenCacheRec(d);

            balance = await tokenCacheRec.fns.balanceOf(wAddress);
        } catch (e) {
            console.error(`Error reading balanceOf ${d.symbol} (${d.identifier}) for wallet ${wAddress}:`, e);
        }

        if (tokenCacheRec && balance !== undefined) {
            balance = adjustDp(balance, tokenCacheRec.decimals, d.decimalPlaces);
        }

        return {deliverable: d, balance};
    }

    private async readNativeDelBalance(
        wAddress: string,
        d: Deliverable
    ): Promise<DeliverableBalance> {
        let tokeBalance: bigint | undefined = undefined;

        try {
            tokeBalance = await this.publicClientReadBalance.getBalance({address: wAddress as Hex});
        } catch (e) {
            console.error(`Error reading balanceOf ${d.symbol} (native) for wallet ${wAddress}:`, e);
        }

        const balance = adjustDp(
            tokeBalance,
            this.nativeCurrency.decimals,
            d.decimalPlaces
        );

        return {deliverable: d, balance};
    }

    private async createTokenCacheRec(tokenAddr: string): Promise<TokenCacheRec> {
        const decimalsBigInt = await this.publicClientReadBalance.readContract({
            address:           tokenAddr as Hex,
            abi:               erc20Abi,
            functionName:      'decimals',
            authorizationList: undefined
        });

        const decimals = Number(decimalsBigInt);

        const tokenCacheRec: TokenCacheRec = {
            address: tokenAddr,
            decimals,
            fns:     {
                balanceOf: account =>
                               this.publicClientReadBalance.readContract({
                                   address:           tokenAddr as Hex,
                                   abi:               erc20Abi,
                                   functionName:      'balanceOf',
                                   args:              [account as Hex],
                                   authorizationList: undefined
                               }),
                allowance: (owner, spender) =>
                               this.publicClientReadBalance.readContract({
                                   address:           tokenAddr as Hex,
                                   abi:               erc20Abi,
                                   functionName:      'allowance',
                                   args:              [owner as Hex, spender as Hex],
                                   authorizationList: undefined
                               })
            }
        };

        this.tokensCache.set(tokenAddr, tokenCacheRec);

        return tokenCacheRec;
    }

    private async getOrCreateTokenCacheRec(d: Deliverable): Promise<TokenCacheRec> {
        let tokenCacheRec = this.tokensCache.get(d.identifier);

        if (!tokenCacheRec) {
            try {
                tokenCacheRec = await this.createTokenCacheRec(d.identifier);
            } catch (e) {
                console.error(`Error reading token ${d.symbol} (${d.identifier}):`, e);

                throw e;
            }
        }

        return tokenCacheRec;
    }

    private createDeliverableList(): Deliverable[] {
        const list: Deliverable[] = [];

        const nativeSymbol = this.nativeCurrency.symbol;

        for (const d of this.dexSecuritiesSubjectService.deliverableMapById.values()) {
            if (d.identifier !== DEX_EMPTY_ADDR || d.symbol === nativeSymbol) {
                list.push(d);
            }
        }

        list.sort(
            (a, b) =>
                a.symbol.localeCompare(b.symbol, undefined, {numeric: true}) || 0
        );

        return list;
    }

    private getBalancesWithPagination(
        wAddress: string,
        deliverables: Deliverable[]
    ): Observable<DeliverableBalance[]> {
        const PAGE_SIZE = 100;
        const MAX_CONCURRENT_REQUESTS = 5;

        const deliverablesPages = chunk(deliverables, PAGE_SIZE);

        return from(deliverablesPages).pipe(
            concatMap((pageDeliverables, pageIndex) => {
                const balanceRequests$ = pageDeliverables.map(d => {
                    return defer<Promise<DeliverableBalance>>(async () =>
                        this.readDeliverableBalance(wAddress, d)
                    );
                });

                return forkJoin(balanceRequests$).pipe(
                    mergeMap(request$ => request$, MAX_CONCURRENT_REQUESTS),
                    toArray(),
                    map(pageResults => ({
                        page: pageIndex + 1,
                        totalPages: deliverablesPages.length,
                        results: pageResults
                    }))
                );
            }),
            reduce(
                (acc: DeliverableBalance[], pageResult: { results: DeliverableBalance[] }) =>
                    acc.concat(pageResult.results),
                [] as DeliverableBalance[]
            )
        );
    }

    private isNativeDel(d: Deliverable): boolean {
        return d.symbol === this.nativeCurrency.symbol;
    }

    private notifyNativeBalanceUpdateNeeded(wAddress: string): void {
        const balanceFetchingTrigger = this.injector.get(BALANCE_FETCHING_TRIGGER, null);

        if (balanceFetchingTrigger) {
            const dNative = this.dexSecuritiesSubjectService.getDeliverableBySymbol(this.nativeCurrency.symbol);

            if (dNative) {
                balanceFetchingTrigger.notifyUpdates({
                    wAddresses: [wAddress],
                    dIds: [dNative.deliverableId]
                });
            }
        }
    }

    private createReadBalancePublicClient(): PublicClient {
        const chainConfig = this.appConfigService.appConfig.dex.chain;
        const chain = defineChain(chainConfig);

        const transport = chain.contracts?.multicall3
            ? http()
            : http(undefined, {
                batch: {
                    wait: 0,
                    batchSize: 100
                }
            });

        return createPublicClient({
            chain,
            transport,
            batch:     {multicall: true}
        }) as any; // A workaround for strange type errors
    }

}
